import React from 'react';
import { logClientError } from '../lib/errorLogging';

// A failed dynamic import() of a route chunk — typically a returning visitor whose cached
// index still points at chunk hashes that a newer deploy has replaced. The fix is simply to
// reload onto the fresh build, so we don't want to show the generic error screen for it.
function isChunkLoadError(error: Error): boolean {
  const msg = `${error?.name || ''} ${error?.message || ''}`;
  return /ChunkLoadError|Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|importing a module script failed/i.test(msg);
}

// Catches errors thrown during render/lifecycle anywhere below it — without this,
// one broken component would blank the entire screen for that visitor with no
// trace left anywhere. Wraps the whole app once, at the root.
export default class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Stale-deploy chunk failure: reload once onto the fresh build instead of stranding the
    // visitor on the error screen. The sessionStorage flag stops a reload loop if the chunk is
    // genuinely gone (e.g. a real 403/404), in which case we fall through to the error screen.
    if (isChunkLoadError(error)) {
      try {
        if (!sessionStorage.getItem('chunkReloaded')) {
          sessionStorage.setItem('chunkReloaded', '1');
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage unavailable (private mode) — fall through to the error screen */ }
    }
    logClientError(error.message, error.stack || info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 14, padding: 24, textAlign: 'center',
          fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif', color: '#241A1D',
        }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Diçka shkoi keq.</div>
          <p style={{ fontSize: 14, color: '#6B7280', maxWidth: 360 }}>
            E kemi regjistruar gabimin. Provo të rifreskosh faqen.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#6B2737', color: 'white', border: 'none', borderRadius: 10,
              padding: '10px 22px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            Rifresko
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
